import Button from './Button.js';
import SettlementFieldList from './SettlementFieldList.js';
import SettlementModalShell from './SettlementModalShell.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';
import { statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

export default function CompanySettlementDueView({
  kind,
  row,
  onClose,
  onSettle,
  canSettle = false,
  onViewPending,
}) {
  if (!row) return null;

  const isPaid = kind === 'paid';
  const pendingId = row.pending_request_id;
  const pendingStatus = row.pending_request_status;
  const title = isPaid ? 'Paid remittance' : 'To-Pay remittance';
  const subtitle = isPaid ? row.shipment_id : row.ml_sheet_id;
  const outstanding = Number(row.amount_outstanding || 0);

  const paidItems = [
    { label: 'GC', value: row.shipment_id },
    ...(row.agent_name || row.agent_user_id
      ? [{ label: 'Agent', value: row.agent_name || row.agent_user_id }]
      : []),
    { label: 'Date', value: formatIndianDate(row.booking_date) },
    {
      label: 'Route',
      value: [row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—',
      span: 2,
    },
    { label: 'Payment mode', value: row.mode_of_payment || '—' },
    { label: 'Status', value: String(row.paid_remit_status || row.status || 'pending') },
    { label: 'Paid', value: fullCurrency(row.paid_amount), mono: true },
    { label: 'Remitted', value: fullCurrency(row.paid_remitted_amount), mono: true },
    ...(Number(row.loading_kept || 0) > 0.009
      ? [{ label: 'Loading kept', value: fullCurrency(row.loading_kept), mono: true }]
      : []),
    { label: 'Outstanding', value: fullCurrency(outstanding), mono: true },
  ];

  const toPayItems = [
    { label: 'ML Sheet', value: row.ml_sheet_id },
    { label: 'Date', value: formatIndianDate(row.created_at) },
    { label: 'Location', value: row.location_name || '—' },
    { label: 'Agent', value: row.agent_name || row.agent_user_id || '—' },
    {
      label: 'GCs',
      value: (row.shipment_ids || []).length
        ? `${row.shipment_ids.length} · ${
            row.shipment_ids.length <= 2
              ? row.shipment_ids.join(', ')
              : `${row.shipment_ids.slice(0, 2).join(', ')} +${row.shipment_ids.length - 2}`
          }`
        : `${row.shipment_count || 0} GC(s)`,
      span: 2,
    },
    { label: 'Total freight', value: fullCurrency(row.total_freight), mono: true },
    {
      label: 'Commission',
      value: `${row.commission_pct != null ? `${row.commission_pct}% · ` : ''}${fullCurrency(row.commission_amount)}${
        row.deduct_commission_from_remit === false
          || row.ml_commission_remit_mode === 'labour_keep_separate'
          ? ' (pay separately)'
          : ''
      }`,
      mono: true,
    },
    ...(Number(row.unloading_on_gcs || row.unloading_kept || 0) > 0.009
      ? [
          {
            label: 'Unloading on GCs',
            value: fullCurrency(row.unloading_on_gcs),
            mono: true,
          },
          {
            label: 'Unloading kept (you)',
            value: fullCurrency(
              row.unloading_kept != null && Number(row.unloading_kept) > 0
                ? row.unloading_kept
                : row.unloading_on_gcs,
            ),
            mono: true,
          },
          {
            label: 'Unloading to company',
            value: fullCurrency(0),
            mono: true,
          },
        ]
      : []),
    { label: 'Net due', value: fullCurrency(row.amount_due), mono: true },
    { label: 'Returned', value: fullCurrency(row.amount_returned), mono: true },
    { label: 'Outstanding', value: fullCurrency(outstanding), mono: true },
    { label: 'Collection', value: String(row.collection_status || 'pending') },
  ];

  return (
    <SettlementModalShell title={title} subtitle={subtitle} onClose={onClose} size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-2.5">
            <p className="text-[11px] text-gray-500">Outstanding</p>
            <p className="font-semibold tabular-nums">{fullCurrency(outstanding)}</p>
          </div>
          {!isPaid ? (
            <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-2.5">
              <p className="text-[11px] text-gray-500">Commission</p>
              <p className="font-semibold tabular-nums">{fullCurrency(row.commission_amount)}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-2.5">
              <p className="text-[11px] text-gray-500">Paid</p>
              <p className="font-semibold tabular-nums">{fullCurrency(row.paid_amount)}</p>
            </div>
          )}
          {pendingStatus ? (
            <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-2.5">
              <p className="text-[11px] text-gray-500">Request</p>
              <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs border ${statusBadge(pendingStatus)}`}>
                {statusLabel(pendingStatus)}
              </span>
            </div>
          ) : null}
        </div>

        <SettlementFieldList items={isPaid ? paidItems : toPayItems} />

        {pendingId ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
            <p>
              A settlement request is {statusLabel(pendingStatus || 'PENDING').toLowerCase()}.
              {pendingStatus === 'HUMAN_REVIEW' ? ' An accountant will review it.' : ''}
            </p>
            {onViewPending ? (
              <button
                type="button"
                className="mt-1.5 font-semibold text-sky-800 underline"
                onClick={() => onViewPending(pendingId)}
              >
                View settlement request {pendingId}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-2.5 py-2 text-xs text-gray-700">
            Submit from Make Settlement on this page — one receipt covers Paid and To-Pay together.
            {canSettle && onSettle ? (
              <div className="mt-2">
                <Button variant="primary" size="sm" onClick={onSettle}>
                  Make Settlement
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </SettlementModalShell>
  );
}
